import React from 'react';
import Select, { components } from 'react-select';
import * as S from './styles';
import { Colors } from '~/config/Colors';
import Tooltip from '~/componentes/Tooltip';

const MAX_LABELS_SHOWN = 2;

const CompactMultiValue = props => {
  const { index, getValue } = props;
  if (index > 0) return null;

  const allSelected = getValue();
  const labelsText = allSelected
    .slice(0, MAX_LABELS_SHOWN)
    .map(option => option.label)
    .join(', ');
  const hasMore = allSelected.length > MAX_LABELS_SHOWN;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
          color: Colors.text_Color_1,
          fontSize: '0.8em',
          fontWeight: '400',
        }}
      >
        {labelsText}
        {hasMore ? '...' : ''}
      </span>
      {allSelected.length > 1 && (
        <span
          style={{
            flexShrink: 0,
            background: Colors.Color4,
            color: '#fff',
            borderRadius: '999px',
            fontSize: '0.7em',
            fontWeight: 700,
            minWidth: '18px',
            height: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px',
          }}
        >
          {allSelected.length}
        </span>
      )}
    </div>
  );
};

const CheckboxOption = props => {
  const { isSelected, label } = props;

  return (
    <components.Option {...props}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {}}
          style={{
            width: '14px',
            height: '14px',
            accentColor: Colors.Color4,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        />
        <span>{label}</span>
      </div>
    </components.Option>
  );
};

const getScrollbarStyles = () => {
  const isDarkTheme = localStorage.getItem('tema') === 'E';

  if (isDarkTheme) {
    return {
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: 'rgba(255, 255, 255, 0.5)',
      },
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1)',
    };
  } else {
    return {
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, 0.05)',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: 'rgba(0, 0, 0, 0.3)',
      },
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.05)',
    };
  }
};

const getOptionStyles = () => {
  const isDarkTheme = localStorage.getItem('tema') === 'E';

  if (isDarkTheme) {
    return {
      selectedBackground: `${Colors.Input_Color_1}80`,
      focusedBackground: 'rgba(255, 255, 255, 0.1)',
      hoverBackground: 'rgba(255, 255, 255, 0.1)',
    };
  } else {
    return {
      selectedBackground: `${Colors.Input_Color_1}80`,
      focusedBackground: 'rgba(0, 0, 0, 0.05)',
      hoverBackground: 'rgba(0, 0, 0, 0.05)',
    };
  }
};

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    width: '100%',
    height: '38px',
    minHeight: '38px',
    backgroundColor: Colors.Input_Color_1,
    border: '1px solid rgb(255 255 255 / 0.15)',
    borderRadius: '8px',
    color: Colors.text_Color_1,
    fontSize: '0.75em',
    fontWeight: '300',
    transition: 'border, background-color 300ms ease-in-out',
    boxShadow: 'none',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.5 : 1,
    '&:hover': {
      border: '1px solid rgb(255 255 255 / 0.25)',
      borderRadius: '8px',
    },
    '&:focus-within': {
      border: '1px solid rgb(255 255 255 / 0.4)',
    },
  }),
  valueContainer: provided => ({
    ...provided,
    padding: '0 18px',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }),
  input: provided => ({
    ...provided,
    color: Colors.text_Color_1,
    fontSize: '0.8em',
    fontWeight: '400',
  }),
  placeholder: provided => ({
    ...provided,
    color: Colors.text_Color_1,
    fontSize: '0.8em',
    fontWeight: '400',
  }),
  singleValue: provided => ({
    ...provided,
    color: Colors.text_Color_1,
    fontSize: '0.8em',
    fontWeight: '400',
  }),
  menu: provided => ({
    ...provided,
    backgroundColor: Colors.Input_Color_1,
    border: '1px solid rgb(255 255 255 / 0.15)',
    borderRadius: '8px',
    zIndex: 9999,
  }),
  menuPortal: provided => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: provided => ({
    ...provided,
    padding: 0,
    ...getScrollbarStyles(),
  }),
  option: (provided, state) => {
    const optionStyles = getOptionStyles();

    return {
      ...provided,
      backgroundColor: state.isSelected
        ? optionStyles.selectedBackground
        : state.isFocused
        ? optionStyles.focusedBackground
        : 'transparent',
      color: Colors.text_Color_1,
      fontSize: '0.75em',
      fontWeight: '300',
      padding: '8px 18px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: optionStyles.hoverBackground,
      },
    };
  },
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: Colors.text_Color_1,
    padding: '8px',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 200ms ease-in-out',
  }),
  clearIndicator: provided => ({
    ...provided,
    color: Colors.text_Color_1,

    padding: '8px',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: `${Colors.Input_Color_1}`,
    border: '1px solid rgb(255 255 255 / 0.15)',
    borderRadius: '6px',
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: Colors.text_Color_1,
    fontSize: '0.8em',
    fontWeight: '400',
  }),
  multiValueRemove: provided => ({
    ...provided,
    color: Colors.text_Color_1,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      color: Colors.text_Color_1,
    },
  }),
};

const InputSelect = React.forwardRef(
  (
    {
      name,
      label,
      error,
      required = false,
      options = [],
      onChange,
      value,
      defaultValue,
      placeholder = 'Selecione uma opção',
      isClearable = false,
      isDisabled = false,
      isSearchable = true,
      isMulti = false,
      menuPortalTarget,
      menuPosition = 'absolute',
      menuPlacement = 'auto',
      menuShouldScrollIntoView = false,
      labelTooltip,
      ...rest
    },
    ref,
  ) => {
    const normalizedOptions = React.useMemo(() => {
      return options.map(option => {
        if (typeof option === 'string' || typeof option === 'number') {
          return { value: option, label: option };
        }
        return option;
      });
    }, [options]);

    const getSelectedOption = React.useCallback(
      currentValue => {
        if (currentValue === null || currentValue === undefined || currentValue === '') {
          return null;
        }
        return (
          normalizedOptions.find(option => String(option.value) === String(currentValue)) || null
        );
      },
      [normalizedOptions],
    );

    const getSelectedOptions = React.useCallback(
      currentValue => {
        const valuesArray = Array.isArray(currentValue) ? currentValue : [];
        return normalizedOptions.filter(option =>
          valuesArray.some(v => String(v) === String(option.value)),
        );
      },
      [normalizedOptions],
    );

    const selectedOption = React.useMemo(() => {
      const currentValue = value !== undefined ? value : defaultValue;
      return isMulti ? getSelectedOptions(currentValue) : getSelectedOption(currentValue);
    }, [value, defaultValue, getSelectedOption, getSelectedOptions, isMulti]);

    const handleChange = selectedOption => {
      if (onChange) {
        const syntheticEvent = {
          target: {
            name,
            value: isMulti
              ? (selectedOption || []).map(option => option.value)
              : selectedOption
              ? selectedOption.value
              : '',
          },
        };
        onChange(syntheticEvent);
      }
    };

    return (
      <S.InputText disabled={isDisabled}>
        {label && (
          <S.Label htmlFor={name}>
            {label}
            {labelTooltip && <Tooltip content={labelTooltip} />}
          </S.Label>
        )}
        <S.InputContainer>
          <Select
            ref={ref}
            inputId={name}
            name={name}
            value={selectedOption}
            onChange={handleChange}
            options={normalizedOptions}
            placeholder={placeholder}
            isClearable={isClearable}
            isDisabled={isDisabled}
            isSearchable={isSearchable}
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            hideSelectedOptions={false}
            components={
              isMulti ? { MultiValue: CompactMultiValue, Option: CheckboxOption } : undefined
            }
            styles={customStyles}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            noOptionsMessage={() => 'Nenhuma opção encontrada'}
            loadingMessage={() => 'Carregando...'}
            menuPlacement={menuPlacement}
            menuShouldScrollIntoView={menuShouldScrollIntoView}
            required={required}
            {...rest}
          />
          {error && <S.Error>{error}</S.Error>}
        </S.InputContainer>
      </S.InputText>
    );
  },
);

InputSelect.displayName = 'InputSelect';

export default InputSelect;
